import React, { useEffect, useState, useRef, useContext, useCallback } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './BlogDetails.css';
import SubscribeBox from '../Component/Sidebar/Subscribe';
import { Helmet } from 'react-helmet';
import { HomeContext } from '../Context/HomeContext';
import { FaEye, FaRegCalendarAlt, } from 'react-icons/fa';
import AdBlockVertical from '../Component/Ads/AdBlockVertical';
import AdBlockHorizontal from '../Component/Ads/AdBlockHorizontal';
import AdBlockHorizontalMultiplex from '../Component/Ads/AdBlockHorizontalMultiplex';

// Secret code feature styles
const secretCodeStyles = `
  /* Timer Styling */
  .timer {
    font-size: 24px;
    font-weight: bold;
    width: 80px;
    height: 80px;
    line-height: 80px;
    margin: 0px auto;
    color: #ff1949;
    border: 4px dashed #ff1949;
    border-radius: 50%;
    display: none; /* Hide by default */
    text-align: center;
  }

  /* button style */
  .scroll-button {
    display: none; /* Hide by default */
    margin: 0px auto;
    padding: 10px 20px;
    background-color: #ff1949;
    color: white;
    border: none;
    border-radius: 5px;
    cursor: pointer;
  }

  .scroll-button i {
    animation: iconFlicker 1s infinite alternate;
  }

  @keyframes iconFlicker {
    0% {
      opacity: 1;
    }
    100% {
      opacity: 0.2;
    }
  }

  .top-section {
    margin-bottom: 20px;
  }

  .bottom-section {
  margin-top: 20px;
  }

  .top-section, .bottom-section {
    display: none; /* Hide sections by default */
    text-align: center;
    padding-top: 5px;
    padding-bottom: 0;
  }

  .bottom-section {
    padding-top: 0;
    padding-bottom: 5px;
  }

  #secret_code_container {
    display: none;
    margin: 20px auto;
    max-width: 400px;
    padding: 20px;
    background: linear-gradient(145deg, #ff3366, #ff0044);
    border-radius: 10px;
    box-shadow: 0 10px 25px rgba(255, 25, 73, 0.3);
    animation: fadeInUp 0.5s ease-out;
  }

  #secret_code {
    font-weight: bold;
    font-size: 24px;
    color: white;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    letter-spacing: 1px;
    margin: 10px 0;
    padding: 15px;
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    border: 2px dashed rgba(255, 255, 255, 0.6);
    cursor: pointer;
    position: relative;
  }

  #secret_code:hover {
    background-color: rgba(255, 255, 255, 0.3);
  }

  .secret-title {
    color: white;
    font-size: 18px;
    margin-bottom: 15px;
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .secret-icon {
    font-size: 32px;
    margin-bottom: 10px;
    color: white;
  }

  .copy-button {
    background-color: white;
    color: #ff1949;
    border: none;
    border-radius: 5px;
    padding: 8px 15px;
    margin-top: 15px;
    cursor: pointer;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    margin-right: auto;
    transition: all 0.2s ease;
  }

  .copy-button:hover {
    background-color: #f0f0f0;
    transform: translateY(-2px);
  }

  .copy-button i {
    margin-right: 5px;
  }

  .copy-tooltip {
    position: absolute;
    background-color: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 5px 10px;
    border-radius: 4px;
    font-size: 14px;
    top: -30px;
    left: 50%;
    transform: translateX(-50%);
    opacity: 0;
    transition: opacity 0.3s;
    pointer-events: none;
  }

  .copy-tooltip.show {
    opacity: 1;
  }
`;

const BlogDetails = () => {
  const { slug } = useParams();
  const location = useLocation();
  const { homeData } = useContext(HomeContext);
  const recentBlogs = homeData?.recentBlogs || [];

  const [blog, setBlog] = useState(null);
  const [blogLoading, setBlogLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [commentForm, setCommentForm] = useState({ full_name: '', email: '', description: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAllComments, setShowAllComments] = useState(false);

  // Timer and secret code state
  const [topTimerCompleted, setTopTimerCompleted] = useState(false);
  const [bottomTimerCompleted, setBottomTimerCompleted] = useState(false);
  const [secretCode, setSecretCode] = useState('');
  const [remainingTopTime, setRemainingTopTime] = useState(0);
  const [remainingBottomTime, setRemainingBottomTime] = useState(0);
  const [showTopSection, setShowTopSection] = useState(false);
  const [showBottomSection, setShowBottomSection] = useState(false);
  const [showSecretCode, setShowSecretCode] = useState(false);

  const topTimerRef = useRef(null);
  const bottomTimerRef = useRef(null);
  const topTimerIntervalRef = useRef(null);
  const bottomTimerIntervalRef = useRef(null);
  const topTimeValueRef = useRef(0);

  const decryptRedirectParam = useCallback(async () => {
    const urlParams = new URLSearchParams(location.search);
    let encryptedPayload = urlParams.get('redirect');

    if (!encryptedPayload) {
      console.log('No redirect parameter found');
      return false;
    }

    // 1. Handle URL encoding issues
    encryptedPayload = decodeURIComponent(encryptedPayload)
      .replace(/\s/g, '+') // Replace spaces with '+' (if any)
      .replace(/-/g, '+') // Optional: If URL used - instead of +
      .replace(/_/g, '/'); // Optional: If URL used * instead of /

    // 2. Add Base64 padding if needed
    const padLength = (4 - (encryptedPayload.length % 4)) % 4;
    encryptedPayload += '='.repeat(padLength);

    const secretKey = '1234567890123456';

    try {
      const binaryData = Uint8Array.from(atob(encryptedPayload), c => c.charCodeAt(0));
      const iv = binaryData.slice(0, 16);
      const ciphertext = binaryData.slice(16);

      const key = await crypto.subtle.importKey(
        'raw',
        new TextEncoder().encode(secretKey),
        { name: 'AES-CBC' },
        false,
        ['decrypt']
      );

      const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-CBC', iv },
        key,
        ciphertext
      );

      const code = new TextDecoder().decode(decrypted);
      setSecretCode(code);
      console.log('Decrypted:', code);

      // Now call initializeTimers directly with the known values rather than using state
      // (Since state updates are asynchronous and won't be reflected immediately)
      initializeTimersWithParams(true, true);

      return true;
    } catch (error) {
      console.error('Decryption failed:', error);
      return false;
    }
  }, [location.search]);

  // Separate function to initialize timers with direct parameters
  const initializeTimersWithParams = (hasRedirectParam, isDecryptionSuccessful) => {
    console.log(`Direct params: ${hasRedirectParam}-${isDecryptionSuccessful}`);

    if (hasRedirectParam && isDecryptionSuccessful) {
      setShowTopSection(true);

      if (document.visibilityState === 'visible') {
        startTopTimer();
      }
    } else {
      console.log('Timer initialization skipped - conditions not met');
    }
  };

  const startTopTimer = () => {
    if (topTimerCompleted) return;

    console.log("Starting top timer");
    const countdown = Math.floor(Math.random() * 51) + 15;
    console.log('countdown: ' + countdown);
    setRemainingTopTime(countdown);

    clearInterval(topTimerIntervalRef.current);

    topTimerIntervalRef.current = setInterval(() => {
      setRemainingTopTime(prev => {
        if (prev <= 1) {
          clearInterval(topTimerIntervalRef.current);
          setTopTimerCompleted(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const startBottomTimer = () => {
    if (bottomTimerCompleted) return;

    console.log("Starting bottom timer");
    const countdown = Math.floor(Math.random() * 51) + 15;
    setRemainingBottomTime(countdown);

    clearInterval(bottomTimerIntervalRef.current);

    bottomTimerIntervalRef.current = setInterval(() => {
      setRemainingBottomTime(prev => {
        if (prev <= 1) {
          clearInterval(bottomTimerIntervalRef.current);
          setBottomTimerCompleted(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const pauseTimers = () => {
    console.log("Pausing timers");
    clearInterval(topTimerIntervalRef.current);
    clearInterval(bottomTimerIntervalRef.current);
    topTimerIntervalRef.current = null;
    bottomTimerIntervalRef.current = null;
  };

  const resumeTimers = () => {
    console.log("Resuming timers, top completed:", topTimerCompleted, "bottom completed:", bottomTimerCompleted);

    if (showTopSection && !topTimerCompleted && remainingTopTime > 0) {
      console.log("Resuming top timer with", remainingTopTime, "seconds left");

      clearInterval(topTimerIntervalRef.current);

      topTimerIntervalRef.current = setInterval(() => {
        setRemainingTopTime(prev => {
          if (prev <= 1) {
            console.log('run');
            clearInterval(topTimerIntervalRef.current);
            setTopTimerCompleted(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    }

    if (showBottomSection && !bottomTimerCompleted && remainingBottomTime > 0) {
      console.log("Resuming bottom timer with", remainingBottomTime, "seconds left");
      clearInterval(bottomTimerIntervalRef.current);

      bottomTimerIntervalRef.current = setInterval(() => {
        setRemainingBottomTime(prev => {
          if (prev <= 1) {
            clearInterval(bottomTimerIntervalRef.current);
            setBottomTimerCompleted(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };

  const handleTopButtonClick = () => {
    setShowTopSection(false);
    setShowBottomSection(true);
    startBottomTimer();

    // Scroll to the bottom section
    setTimeout(() => {
      const bottomSection = document.querySelector('.bottom-section');
      if (bottomSection) {
        const scrollPosition = bottomSection.offsetTop - (window.innerHeight - bottomSection.offsetHeight);
        window.scrollTo({
          top: scrollPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const handleBottomButtonClick = () => {
    setShowSecretCode(true);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(secretCode).then(() => {
      // Show "Copied!" tooltip
      const tooltip = document.querySelector('.copy-tooltip');
      if (tooltip) {
        tooltip.classList.add('show');
        setTimeout(() => {
          tooltip.classList.remove('show');
        }, 2000);
      }
    }).catch(err => {
      console.error('Error copying text: ', err);
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCommentForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    const { full_name, email, description } = commentForm;

    if (!full_name || !email || !description) {
      toast.error('Please fill in all fields.');
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await axios.post(
        `https://api.funfillia.com/api/v2/website/blog/comment/store`,
        {
          blog_id: blog?.id,
          full_name,
          email,
          description,
        },
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'Client-Domain': 'www.funfillia.com',
          },
        }
      );

      const newComment = res.data.comment;
      newComment._localPreview = true;

      setComments(prev => [newComment, ...prev]);
      setCommentForm({ full_name: '', email: '', description: '' });
      toast.success(res.data.message || 'Comment posted successfully!');
    } catch (err) {
      toast.error('Failed to post comment. Try again later.');
      console.error("Comment submission error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchBlogData = useCallback(async () => {
    setBlogLoading(true);
    try {
      const options = {
        headers: {
          Accept: 'application/json',
          'Client-Domain': 'www.funfillia.com',
        },
      };
      const res = await axios.get(`https://api.funfillia.com/api/v2/website/blogs/${slug}`, options);
      setBlog(res.data.blog || null);

      const approvedComments = (res.data.blog.comments || [])
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      setComments(approvedComments);
    } catch (error) {
      console.error('Error fetching blog data:', error);
    } finally {
      setBlogLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchBlogData();

    // Check for redirect parameter on component mount
    const checkRedirectParam = async () => {
      await decryptRedirectParam();
    };

    checkRedirectParam();

  }, [slug, fetchBlogData, decryptRedirectParam]);


  useEffect(() => {
    // Visibility change listener
    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === 'visible';
      if (!isVisible) {
        console.log('Page hidden - pausing timers');
        pauseTimers();
      } else {
        console.log('Page visible - resuming timers');
        resumeTimers();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
  }, [topTimerCompleted, bottomTimerCompleted, remainingTopTime, remainingBottomTime]);

  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = secretCodeStyles;
    document.head.appendChild(styleElement);
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  useEffect(() => {
    fetchBlogData();
  }, [slug, fetchBlogData]);

  useEffect(() => {
    if (blog) {
      document.title = `${blog.title} - Funfillia`;
    }
  }, [blog]);

  const visibleComments = showAllComments
    ? comments
    : comments.slice(0, 3);

  return (
    <>
      {blog && (
        <Helmet>
          <title>{`${blog.title} - Funfillia`}</title>
          <meta name="title" content={`${blog.title} - Funfillia`} />
          <meta name="description" content={blog.short_description} />
          <meta name="keywords" content={blog.tags} />
          <meta property="og:type" content="article" />
          <meta property="og:url" content={window.location.href} />
          <meta property="og:title" content={`${blog.title} | Funfillia`} />
          <meta property="og:description" content={blog.short_description} />
          <meta property="og:image" content={`https://api.funfillia.com/${blog.image_500x500}`} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`${blog.title} | Funfillia`} />
          <meta name="twitter:description" content={blog.short_description} />
          <meta name="twitter:image" content={`https://api.funfillia.com/${blog.image_500x500}`} />
          <link rel="canonical" href={window.location.href} />
        </Helmet>
      )}

      <div className="blog-detail-container my-4">
        <ToastContainer position="bottom-right" autoClose={3000} />

        <div className="main-blog px-md-3">
          {blogLoading ? (
            <div className="blog-loading">
              <div className="blog-spinner"></div>
              <p className="blog-loading-text">Loading blog, please wait...</p>
            </div>
          ) : !blog ? (
            <div className="blog-not-found">Blog not found</div>
          ) : (
            <>

              {showTopSection && (
                <section className="top-section" style={{ display: showTopSection ? 'block' : 'none' }}>
                  <div className="top-content"></div>
                  <div
                    className="timer top-timer"
                    ref={topTimerRef}
                    style={{ display: !topTimerCompleted ? 'block' : 'none' }}
                  >
                    {remainingTopTime}
                  </div>
                  <button
                    className="scroll-button top-button"
                    onClick={handleTopButtonClick}
                    style={{ display: topTimerCompleted ? 'block' : 'none' }}
                  >
                    <i className="fas fa-hand-point-right"></i>
                    Jump to Secret Code
                    <i className="fas fa-hand-point-left"></i>
                  </button>
                </section>
              )}
              <h1 className="blog-title">{blog.title}</h1>
              <div className="meta-info">
                <span><FaRegCalendarAlt /> {new Date(blog.published_at).toLocaleDateString()}</span>
                <span><FaEye /> {blog.views_count} views</span>
                {/* <a href="#comments-section"><span><FaCommentDots /> {blog.comments_count} comment{comments.length !== 1 ? 's' : ''}</span></a> */}
              </div>

              <img src={`https://api.funfillia.com/${blog.image_850x500}`} alt={blog.title} title={blog.title} className="blog-banner" />

              <AdBlockHorizontalMultiplex />

              <div className="blog-content" dangerouslySetInnerHTML={{ __html: blog.content }} />

              {/* <div className="comments-section" id="comments-section">
                <h3>Leave a Comment</h3>

                <form onSubmit={handleCommentSubmit} className="comment-form">
                  <input type="text" name="full_name" placeholder="Your name" value={commentForm.full_name} onChange={handleInputChange} required />
                  <input type="email" name="email" placeholder="Your email" value={commentForm.email} onChange={handleInputChange} required />
                  <textarea name="description" placeholder="Write your comment..." value={commentForm.description} onChange={handleInputChange} rows={4} required />
                  <button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Posting...' : 'Post Comment'}
                  </button>
                </form>

                <p><strong>{blog.comments_count}</strong> Comment{comments.length !== 1 ? 's' : ''} so far</p>

                {visibleComments.length > 0 && (
                  <div className="comment-list">
                    {visibleComments.map((c, i) => (
                      <div className="comment-item" key={i}>
                        <p><strong>{c.full_name}</strong></p>
                        <p>{c.description}</p>
                        <span className="comment-date">
                          🕒 {new Date(c.created_at || Date.now()).toLocaleString()}
                        </span>
                      </div>
                    ))}

                    {comments.length > 3 && (
                      <button className="toggle-comments-btn" onClick={() => setShowAllComments(!showAllComments)}>
                        {showAllComments ? 'Show Less' : 'Show More'}
                      </button>
                    )}
                  </div>
                )}
              </div> */}

              <AdBlockHorizontal />

              {showBottomSection && (
                <section className="bottom-section" style={{ display: showBottomSection ? 'block' : 'none' }}>
                  <div className="bottom-content"></div>
                  <div
                    className="timer bottom-timer"
                    ref={bottomTimerRef}
                    style={{ display: !bottomTimerCompleted ? 'block' : 'none' }}
                  >
                    {remainingBottomTime}
                  </div>

                  <button
                    className="scroll-button bottom-button"
                    onClick={handleBottomButtonClick}
                    style={{ display: bottomTimerCompleted && !showSecretCode ? 'block' : 'none' }}
                  >
                    <i className="fas fa-hand-point-right"></i>
                    Reveal Secret Code
                    <i className="fas fa-hand-point-left"></i>
                  </button>

                  <div
                    id="secret_code_container"
                    style={{ display: showSecretCode ? 'block' : 'none' }}
                  >
                    <div className="secret-icon"><i className="fas fa-unlock-alt"></i></div>
                    <div className="secret-title">Your Secret Code:</div>
                    <div id="secret_code" title="Click to copy" onClick={copyToClipboard}>
                      {secretCode}
                      <span className="copy-tooltip">Copied!</span>
                    </div>
                    <button className="copy-button" id="copy_button" onClick={copyToClipboard}>
                      <i className="fas fa-copy"></i>&nbsp; Copy Code
                    </button>
                  </div>
                </section>
              )}
            </>
          )}
        </div>

        <aside className="recent-sidebar">
          <div className="sidebar-title">Top Blogs</div>
          {recentBlogs.map((item) => (
            <div className="sidebar-item" key={item.id}>
              <Link to={`/blog/${item.slug}`} className="recent-link">
                <img src={`https://api.funfillia.com/${item.image_200x200}`} alt={item.title} title={item.title} />
                <div className="sidebar-text">
                  <p className="sidebar-headline">{item.title}</p>
                  <p className="sidebar-date">
                    <FaRegCalendarAlt /> {new Date(item.published_at).toLocaleDateString()} &nbsp;&nbsp;&nbsp;
                    <FaEye /> {item.views_count} views
                  </p>
                </div>
              </Link>
            </div>
          ))}
          <br></br>
          
          <AdBlockVertical/>
        </aside>

      </div>

      <SubscribeBox />
    </>
  );
};

export default BlogDetails;
